import classnames from 'classnames';
import React, { useState } from 'react';
import { trackNavigationLinkClicked } from '../../../tracking/events';
import { isKeyboardEvent } from '../../../util';
import { Spinner } from '../Spinner';
import styles from './Button.module.scss';

export type ButtonType = 'primary' | 'secondary' | 'link' | 'transparent' | 'ghost';
export type ButtonSize = 'tiny' | 'small' | 'medium' | 'large';

export interface ButtonBaseProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'styles'> {
  text: string | React.ReactElement;
  subText?: string;
  size?: ButtonSize;
  buttonType?: ButtonType;
}

export interface ButtonTrackingProps {
  dataTrackLabel?: string;
  dataTrackLocation?: string;
}

export interface ButtonStateProps {
  disabled?: boolean;
  loading?: boolean;
}

export interface ButtonStyleProps {
  fullWidth?: boolean;
  noMargin?: boolean;
  labelStyleOptions?: {
    underline?: boolean;
    uppercase?: boolean;
    color?: string;
  };
}

export interface ButtonProps extends ButtonBaseProps, ButtonTrackingProps, ButtonStateProps, ButtonStyleProps {}

export const Button: React.FC<ButtonProps> = (props) => {
  const {
    size,
    buttonType: buttonTypeProp,
    labelStyleOptions,
    fullWidth,
    noMargin,
    onClick,
    tabIndex,
    disabled,
    text,
    subText,
    loading,
    dataTrackLabel,
    dataTrackLocation,
    ...rest
  } = props;

  const buttonType = buttonTypeProp ?? 'secondary';
  const buttonSize = size ?? 'medium';
  const [hovering, setHovering] = useState<boolean>(false);
  const buttonOnClick = (e: React.SyntheticEvent) => {
    if (isKeyboardEvent(e)) {
      if (e.key !== 'Enter') {
        return;
      }
    }

    if (dataTrackLabel && dataTrackLocation) {
      trackNavigationLinkClicked(dataTrackLabel, dataTrackLocation);
    }
    if (onClick && !loading && !disabled) {
      onClick?.(e as React.MouseEvent<HTMLButtonElement>);
      e.preventDefault();
    }
  };

  const onMouseEnter = () => {
    setHovering(true);
  };
  const onMouseLeave = () => {
    setHovering(false);
  };

  const optionalStyles: React.CSSProperties = {};
  if (labelStyleOptions?.color && !hovering && !disabled) {
    optionalStyles.color = labelStyleOptions?.color;
  }
  if (fullWidth) {
    optionalStyles.width = '100%';
  }
  if (noMargin) {
    optionalStyles.margin = 'unset';
  }

  return (
    <button
      {...rest}
      tabIndex={tabIndex ?? 0}
      onClick={buttonOnClick}
      onKeyDown={buttonOnClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={disabled}
      className={classnames(styles.root, {
        [styles.underlineLabel]: buttonType === 'link' && labelStyleOptions?.underline,
        [styles.uppercaseLabel]: buttonType === 'link' && labelStyleOptions?.uppercase,
        [styles.tiny]: buttonSize === 'tiny',
        [styles.small]: buttonSize === 'small',
        [styles.medium]: buttonSize === 'medium',
        [styles.large]: buttonSize === 'large',
        [styles.primary]: buttonType === 'primary',
        [styles.secondary]: buttonType === 'secondary',
        [styles.transparent]: buttonType === 'transparent',
        [styles.link]: buttonType === 'link',
        [styles.loading]: loading,
        [styles.windowsOs]: isWindowsOs(),
      })}
      style={optionalStyles}
    >
      {loading && (
        <>
          <Spinner
            visible={true}
            light={buttonType !== 'primary'}
            forButton={true}
            styleOverrides={size === 'small' ? { spinner: { transform: 'scale(0.25)' } } : {}}
          />
        </>
      )}
      {!loading && (
        <>
          <span>{text}</span>
          {subText && <span className={styles.subText}>{subText}</span>}
        </>
      )}
    </button>
  );
};

const isWindowsOs = () => {
  const platform = (navigator as unknown as any)?.userAgentData?.platform || navigator?.platform;
  return platform.indexOf('Win') > -1;
};

export default Button;
